import { useMemo, useState } from "react";
import {
  CheckCircle2,
  Lock,
  Play,
  Eye,
  Star,
  Map as MapIcon,
  List,
  Building2,
  Bird,
  Search,
  Lightbulb,
  Palette,
  Coins,
  Megaphone,
  Mic,
  Trophy,
} from "lucide-react";

/**
 * CEOCityMap — CEO City 3.0. A living mini-city / entrepreneurship theme park
 * for grades 2–5. Frontend-only. Pure inline SVG + CSS animations.
 *
 * Layout strategy:
 *  - 20:9 canvas on desktop, min-width 1100px.
 *  - Horizontal scroll wrapper on narrow viewports keeps landmarks tappable.
 *  - Landmarks positioned in an S-curve with generous vertical separation so
 *    labels never overlap the next building (this fixes the #7-over-#6 bug).
 *  - Cream sidewalk road with dashed-gold centerline, stepping stones, and
 *    waypoint pips.
 *  - LemonBot Jr. guide with a state-aware speech bubble. "Start here!" only
 *    shows when no progress; otherwise a "Next Stop →" pennant tracks the
 *    next recommended mission.
 */

// Static Tailwind-safe palettes (full classnames so PurgeCSS keeps them).
const PALETTE = {
  yellow:  { solid: "bg-yellow-400",  ring: "ring-yellow-500",  text: "text-yellow-900",  stroke: "#ca8a04", body: "#facc15" },
  orange:  { solid: "bg-orange-400",  ring: "ring-orange-500",  text: "text-orange-900",  stroke: "#c2410c", body: "#fb923c" },
  sky:     { solid: "bg-sky-400",     ring: "ring-sky-500",     text: "text-sky-900",     stroke: "#0369a1", body: "#38bdf8" },
  amber:   { solid: "bg-amber-400",   ring: "ring-amber-500",   text: "text-amber-900",   stroke: "#b45309", body: "#f59e0b" },
  rose:    { solid: "bg-rose-400",    ring: "ring-rose-500",    text: "text-rose-900",    stroke: "#be123c", body: "#fb7185" },
  emerald: { solid: "bg-emerald-400", ring: "ring-emerald-500", text: "text-emerald-900", stroke: "#047857", body: "#34d399" },
  violet:  { solid: "bg-violet-400",  ring: "ring-violet-500",  text: "text-violet-900",  stroke: "#6d28d9", body: "#a78bfa" },
};

// Landmark SVGs — richer, more architectural versions of each mission building.
const LANDMARKS = {
  cityHall: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="42" ry="3.5" fill="#00000022" />
      <path d="M50 2 L50 18" stroke={accent} strokeWidth="2" />
      <path d="M50 2 L60 6 L50 10 Z" fill="#ef4444" />
      {/* Dome */}
      <path d="M30 34 Q50 10 70 34 Z" fill={fill} stroke={accent} strokeWidth="1.5" />
      <ellipse cx="50" cy="34" rx="22" ry="3" fill={accent} />
      {/* Main body */}
      <rect x="14" y="34" width="72" height="44" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Columns */}
      {[20, 32, 44, 56, 68, 80].map((x) => (
        <g key={x}>
          <rect x={x} y="42" width="4" height="32" fill="#ffffff" />
          <rect x={x - 1} y="40" width="6" height="3" fill={accent} />
          <rect x={x - 1} y="74" width="6" height="3" fill={accent} />
        </g>
      ))}
      {/* Door */}
      <rect x="44" y="58" width="12" height="16" fill={accent} rx="1" />
      <circle cx="52" cy="66" r="0.8" fill="#ffffff" />
      {/* Steps */}
      <rect x="10" y="78" width="80" height="2.5" fill={accent} rx="0.5" />
      <rect x="6"  y="81" width="88" height="2.5" fill={accent} opacity="0.75" rx="0.5" />
      {/* "HALL" sign */}
      <rect x="38" y="42" width="24" height="6" fill="#ffffff" stroke={accent} strokeWidth="0.6" rx="0.6" />
      <text x="50" y="47" textAnchor="middle" fontSize="4.5" fontWeight="900" fill={accent}>CEO HALL</text>
    </svg>
  ),
  treeNest: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Tree trunk with grain */}
      <rect x="44" y="46" width="12" height="40" fill="#78350f" rx="1" />
      <path d="M46 52 Q48 58 46 66 Q48 74 46 82" fill="none" stroke="#451a03" strokeWidth="0.6" />
      <path d="M54 50 Q52 58 54 66 Q52 74 54 82" fill="none" stroke="#451a03" strokeWidth="0.6" />
      {/* Foliage layers */}
      <circle cx="50" cy="36" r="30" fill="#16a34a" />
      <circle cx="28" cy="28" r="18" fill="#22c55e" />
      <circle cx="72" cy="28" r="18" fill="#22c55e" />
      <circle cx="50" cy="18" r="16" fill="#4ade80" />
      {/* Treehouse */}
      <rect x="32" y="36" width="36" height="24" rx="3" fill={fill} stroke={accent} strokeWidth="1.5" />
      <polygon points="30,36 50,22 70,36" fill={accent} />
      <rect x="30" y="36" width="40" height="2" fill={accent} />
      {/* Round door */}
      <circle cx="50" cy="48" r="5" fill="#78350f" />
      <circle cx="50" cy="48" r="5" fill="none" stroke={accent} strokeWidth="1" />
      <rect x="49" y="48" width="2" height="4" fill={accent} opacity="0.4" />
      {/* Ladder */}
      <path d="M48 60 L48 80 M52 60 L52 80 M48 66 L52 66 M48 72 L52 72 M48 78 L52 78" stroke="#78350f" strokeWidth="1" />
      {/* Birds */}
      <g>
        <circle cx="22" cy="14" r="3" fill="#fb923c" />
        <path d="M20 13 L17 12" stroke="#fb923c" strokeWidth="1" />
        <circle cx="22.6" cy="13" r="0.5" fill="#000" />
      </g>
      <g>
        <circle cx="78" cy="16" r="3" fill="#38bdf8" />
        <path d="M80 15 L83 14" stroke="#38bdf8" strokeWidth="1" />
        <circle cx="77.4" cy="15" r="0.5" fill="#000" />
      </g>
    </svg>
  ),
  lab: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Bubbles rising */}
      <circle cx="50" cy="10" r="3" fill="#bae6fd" opacity="0.8" />
      <circle cx="54" cy="4"  r="2" fill="#bae6fd" opacity="0.7" />
      <circle cx="46" cy="6"  r="1.5" fill="#bae6fd" opacity="0.6" />
      {/* Chimney */}
      <rect x="46" y="14" width="8" height="10" fill={accent} rx="0.5" />
      {/* Roof */}
      <rect x="14" y="26" width="72" height="6" fill={accent} rx="0.5" />
      {/* Main building */}
      <rect x="18" y="32" width="64" height="48" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Windows (checker) */}
      {[22, 34, 66, 78].map((x, i) => (
        <rect key={i} x={x} y="38" width="10" height="10" fill="#bae6fd" stroke={accent} strokeWidth="0.6" rx="1" />
      ))}
      {/* Beaker */}
      <g>
        <rect x="42" y="42" width="16" height="4" fill={accent} rx="0.5" />
        <path d="M44 46 L44 58 L36 74 L64 74 L56 58 L56 46 Z" fill="#ffffffdd" stroke={accent} strokeWidth="1" />
        <rect x="37" y="66" width="26" height="8" fill="#60a5fa" opacity="0.75" />
        <circle cx="42" cy="70" r="1.2" fill="#ffffff" opacity="0.8" />
        <circle cx="52" cy="72" r="0.8" fill="#ffffff" opacity="0.8" />
      </g>
      {/* Door */}
      <rect x="22" y="60" width="10" height="20" fill={accent} rx="1" />
      <rect x="68" y="60" width="10" height="20" fill={accent} rx="1" />
      {/* LAB sign */}
      <rect x="40" y="32" width="20" height="6" fill="#ffffff" stroke={accent} strokeWidth="0.6" rx="0.8" />
      <text x="50" y="37" textAnchor="middle" fontSize="4.5" fontWeight="900" fill={accent}>LAB</text>
    </svg>
  ),
  factory: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Smoke puffs */}
      <circle cx="72" cy="10" r="4" fill="#e5e7eb" opacity="0.9" />
      <circle cx="78" cy="4"  r="3" fill="#e5e7eb" opacity="0.85" />
      <circle cx="68" cy="4"  r="2.5" fill="#e5e7eb" opacity="0.7" />
      {/* Smokestack */}
      <rect x="68" y="14" width="10" height="22" fill={accent} rx="0.5" />
      <rect x="66" y="14" width="14" height="3" fill={accent} rx="0.5" />
      {/* Sawtooth roof */}
      {[14, 28, 42, 56, 70].map((x, i) => (
        <polygon key={i} points={`${x},40 ${x + 7},28 ${x + 14},40`} fill={fill} stroke={accent} strokeWidth="1.2" />
      ))}
      {/* Main body */}
      <rect x="14" y="40" width="72" height="40" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Windows */}
      <rect x="22" y="50" width="10" height="8" fill="#bae6fd" stroke={accent} strokeWidth="0.6" rx="1" />
      <rect x="36" y="50" width="10" height="8" fill="#bae6fd" stroke={accent} strokeWidth="0.6" rx="1" />
      <rect x="54" y="50" width="10" height="8" fill="#bae6fd" stroke={accent} strokeWidth="0.6" rx="1" />
      <rect x="68" y="50" width="10" height="8" fill="#bae6fd" stroke={accent} strokeWidth="0.6" rx="1" />
      {/* Door with gear */}
      <rect x="42" y="62" width="16" height="18" fill={accent} rx="1" />
      <circle cx="50" cy="71" r="3" fill="#ffffff" />
      <circle cx="50" cy="71" r="2" fill={accent} />
      {/* Lightbulb on roof */}
      <g transform="translate(22,22)">
        <circle cx="0" cy="0" r="5" fill="#fde047" stroke={accent} strokeWidth="1" />
        <rect x="-2" y="4" width="4" height="2" fill={accent} />
        <line x1="0" y1="-7" x2="0" y2="-10" stroke="#fbbf24" strokeWidth="1.2" />
        <line x1="-6" y1="0" x2="-10" y2="0" stroke="#fbbf24" strokeWidth="1.2" />
        <line x1="6" y1="0" x2="10" y2="0" stroke="#fbbf24" strokeWidth="1.2" />
      </g>
    </svg>
  ),
  studio: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Roof */}
      <polygon points="10,38 50,12 90,38" fill={accent} />
      <rect x="10" y="36" width="80" height="4" fill={accent} rx="0.5" />
      {/* Main body */}
      <rect x="14" y="40" width="72" height="40" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Stained-glass windows */}
      <rect x="22" y="50" width="14" height="14" fill="#f472b6" stroke="#ffffff" strokeWidth="1.5" rx="1.5" />
      <rect x="43" y="50" width="14" height="14" fill="#60a5fa" stroke="#ffffff" strokeWidth="1.5" rx="1.5" />
      <rect x="64" y="50" width="14" height="14" fill="#34d399" stroke="#ffffff" strokeWidth="1.5" rx="1.5" />
      {/* Door */}
      <rect x="42" y="66" width="16" height="14" fill={accent} rx="1" />
      <circle cx="46" cy="73" r="0.6" fill="#fde047" />
      {/* Palette sign on roof */}
      <g transform="translate(50,24)">
        <ellipse cx="0" cy="0" rx="10" ry="7" fill="#ffffff" stroke={accent} strokeWidth="1" />
        <ellipse cx="4" cy="0" rx="2" ry="1.5" fill={accent} opacity="0.3" />
        <circle cx="-5" cy="-2" r="1.5" fill="#ef4444" />
        <circle cx="0" cy="-3" r="1.5" fill="#3b82f6" />
        <circle cx="5" cy="-1" r="1.5" fill="#22c55e" />
        <circle cx="-3" cy="3" r="1.5" fill="#eab308" />
        <circle cx="3" cy="3" r="1.5" fill="#a855f7" />
      </g>
      {/* Paintbrush on side */}
      <rect x="80" y="68" width="2" height="12" fill="#78350f" transform="rotate(15 81 72)" />
      <path d="M82 68 L86 66 L86 72 Z" fill="#ef4444" transform="rotate(15 84 69)" />
    </svg>
  ),
  market: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Awning with stripes */}
      <path d="M8 42 Q50 20 92 42 L88 44 L12 44 Z" fill={accent} />
      <path d="M14 44 Q18 36 22 44 M26 44 Q30 34 34 44 M38 44 Q42 32 46 44 M50 44 Q54 32 58 44 M62 44 Q66 34 70 44 M74 44 Q78 36 82 44" fill="#ffffff" opacity="0.85" />
      {/* Building */}
      <rect x="14" y="44" width="72" height="36" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Counter with items */}
      <rect x="14" y="62" width="72" height="4" fill={accent} opacity="0.3" />
      <g>
        {[22, 36, 50, 64, 78].map((x, i) => (
          <g key={i}>
            <circle cx={x} cy="56" r="4" fill="#fde047" stroke={accent} strokeWidth="1" />
            <text x={x} y="58.5" textAnchor="middle" fontSize="5" fontWeight="900" fill={accent}>$</text>
          </g>
        ))}
      </g>
      {/* Price tag hanging */}
      <g transform="translate(20,44)">
        <path d="M0 0 L-4 8 L4 12 L8 4 Z" fill="#fbbf24" stroke={accent} strokeWidth="0.8" />
        <circle cx="1" cy="4" r="1" fill="#ffffff" />
      </g>
      {/* Door */}
      <rect x="44" y="66" width="12" height="14" fill={accent} rx="1" />
      {/* "SHOP" sign */}
      <rect x="36" y="44" width="28" height="6" fill="#ffffff" stroke={accent} strokeWidth="0.6" rx="0.8" />
      <text x="50" y="49" textAnchor="middle" fontSize="4.5" fontWeight="900" fill={accent}>MARKET</text>
    </svg>
  ),
  billboard: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Plaza floor */}
      <ellipse cx="50" cy="80" rx="42" ry="6" fill="#e7e5e4" />
      <ellipse cx="50" cy="80" rx="42" ry="6" fill="none" stroke="#a8a29e" strokeWidth="0.6" strokeDasharray="2 2" />
      {/* Billboard supports */}
      <rect x="40" y="68" width="3" height="16" fill="#78350f" />
      <rect x="57" y="68" width="3" height="16" fill="#78350f" />
      {/* Billboard face */}
      <rect x="22" y="20" width="56" height="48" fill={fill} stroke={accent} strokeWidth="1.5" rx="3" />
      <rect x="22" y="20" width="56" height="10" fill={accent} rx="3" />
      <text x="50" y="28" textAnchor="middle" fontSize="6" fontWeight="900" fill="#ffffff">NEWS</text>
      <rect x="28" y="34" width="44" height="5" fill="#ffffff" opacity="0.9" rx="0.8" />
      <rect x="28" y="42" width="36" height="4" fill="#ffffff" opacity="0.7" rx="0.8" />
      <rect x="28" y="48" width="40" height="4" fill="#ffffff" opacity="0.7" rx="0.8" />
      <rect x="28" y="54" width="30" height="4" fill="#ffffff" opacity="0.7" rx="0.8" />
      {/* Megaphone icon */}
      <g transform="translate(64, 58)">
        <path d="M0 0 L10 -4 L10 6 L0 2 Z" fill="#ef4444" stroke={accent} strokeWidth="0.6" />
        <rect x="-4" y="0" width="4" height="2" fill={accent} />
      </g>
      {/* Side lamps */}
      <g transform="translate(14, 72)">
        <rect x="-0.5" y="0" width="1" height="10" fill="#44403c" />
        <circle cx="0" cy="-2" r="2.5" fill="#fde047" />
      </g>
      <g transform="translate(86, 72)">
        <rect x="-0.5" y="0" width="1" height="10" fill="#44403c" />
        <circle cx="0" cy="-2" r="2.5" fill="#fde047" />
      </g>
    </svg>
  ),
  stage: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Arch */}
      <path d="M12 26 Q50 6 88 26" fill="none" stroke={accent} strokeWidth="2.5" />
      {/* Lights on arch */}
      <circle cx="22" cy="20" r="2" fill="#fde047" />
      <circle cx="35" cy="14" r="2" fill="#fde047" />
      <circle cx="50" cy="10" r="2.5" fill="#fde047" />
      <circle cx="65" cy="14" r="2" fill="#fde047" />
      <circle cx="78" cy="20" r="2" fill="#fde047" />
      {/* Back curtain */}
      <rect x="14" y="26" width="72" height="42" fill={accent} rx="2" />
      {[20, 28, 36, 44, 52, 60, 68, 76].map((x) => (
        <path key={x} d={`M${x} 26 Q${x + 2} 40 ${x} 68`} fill="none" stroke="#ffffff22" strokeWidth="1" />
      ))}
      {/* Stage floor */}
      <rect x="10" y="68" width="80" height="12" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      <rect x="10" y="68" width="80" height="3" fill={accent} opacity="0.25" />
      {/* Microphone stand */}
      <g transform="translate(50,58)">
        <rect x="-1" y="0" width="2" height="12" fill="#1f2937" />
        <ellipse cx="0" cy="-2" rx="4" ry="5" fill="#4b5563" />
        <ellipse cx="0" cy="-2" rx="2.4" ry="3" fill="#1f2937" />
        <rect x="-3" y="12" width="6" height="1.5" fill="#1f2937" rx="0.5" />
      </g>
      {/* Spotlight beam */}
      <path d="M30 26 L40 68 L60 68 L70 26 Z" fill="#fde04722" />
      {/* Sign */}
      <rect x="30" y="64" width="40" height="5" fill="#ffffff" stroke={accent} strokeWidth="0.6" rx="0.8" />
      <text x="50" y="68" textAnchor="middle" fontSize="4" fontWeight="900" fill={accent}>PITCH STAGE</text>
    </svg>
  ),
  gradHall: ({ fill, accent }) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
      <ellipse cx="50" cy="86" rx="40" ry="3.5" fill="#00000022" />
      {/* Sunburst */}
      <g opacity="0.5">
        {Array.from({ length: 10 }).map((_, i) => {
          const a = -Math.PI / 2 + (i * Math.PI) / 9;
          const x2 = 50 + Math.cos(a) * 50;
          const y2 = 40 + Math.sin(a) * 50;
          return <line key={i} x1="50" y1="40" x2={x2} y2={y2} stroke="#fde047" strokeWidth="2" strokeLinecap="round" />;
        })}
      </g>
      {/* Confetti */}
      <rect x="14" y="10" width="2" height="4" fill="#ef4444" transform="rotate(20 15 12)" />
      <rect x="84" y="8"  width="2" height="4" fill="#3b82f6" transform="rotate(-30 85 10)" />
      <rect x="20" y="22" width="2" height="4" fill="#22c55e" transform="rotate(45 21 24)" />
      <rect x="78" y="24" width="2" height="4" fill="#a855f7" transform="rotate(-45 79 26)" />
      {/* Roof */}
      <polygon points="12,40 50,14 88,40" fill="#fde047" stroke={accent} strokeWidth="1.5" />
      <rect x="48" y="16" width="4" height="4" fill={accent} />
      {/* Building */}
      <rect x="16" y="40" width="68" height="40" fill={fill} stroke={accent} strokeWidth="1.5" rx="2" />
      {/* Columns */}
      <rect x="22" y="48" width="4" height="28" fill="#ffffff" />
      <rect x="74" y="48" width="4" height="28" fill="#ffffff" />
      {/* Trophy */}
      <g transform="translate(50, 56)">
        <rect x="-8" y="12" width="16" height="3" fill={accent} rx="0.5" />
        <rect x="-4" y="15" width="8"  height="7" fill={accent} />
        <path d="M-10 -6 Q-10 8 0 8 Q10 8 10 -6 L10 -10 L-10 -10 Z" fill="#fde047" stroke={accent} strokeWidth="1" />
        <path d="M-10 -6 Q-16 -6 -16 -1 Q-16 4 -11 4" fill="none" stroke={accent} strokeWidth="1" />
        <path d="M10 -6 Q16 -6 16 -1 Q16 4 11 4" fill="none" stroke={accent} strokeWidth="1" />
        <path d="M-4 -8 L-2 -4 L2 -4 L4 -8" fill="none" stroke={accent} strokeWidth="0.8" />
      </g>
      {/* Star on roof peak */}
      <polygon points="50,20 52,26 58,26 53,30 55,36 50,32 45,36 47,30 42,26 48,26" fill="#ffffff" stroke={accent} strokeWidth="0.8" />
    </svg>
  ),
};

const THEMES = {
  1: { color: "yellow",  label: "CEO Hall",        art: "cityHall",  Icon: Building2 },
  2: { color: "orange",  label: "Biz Birds Nest",  art: "treeNest",  Icon: Bird },
  3: { color: "sky",     label: "Problem Lab",     art: "lab",       Icon: Search },
  4: { color: "amber",   label: "Idea Factory",    art: "factory",   Icon: Lightbulb },
  5: { color: "rose",    label: "Brand Studio",    art: "studio",    Icon: Palette },
  6: { color: "emerald", label: "Money Market",    art: "market",    Icon: Coins },
  7: { color: "violet",  label: "Billboard Plaza", art: "billboard", Icon: Megaphone },
  8: { color: "orange",  label: "Pitch Stage",     art: "stage",     Icon: Mic },
  9: { color: "yellow",  label: "Graduation Hall", art: "gradHall",  Icon: Trophy },
};

// Spaced-out positions on 100x100 viewbox — each landmark owns a clean lane.
// Fixes the #7-over-#6 label collision by pushing #7 down into the foreground.
const POSITIONS = {
  1: { x: 6,  y: 78 },
  2: { x: 17, y: 54 },
  3: { x: 28, y: 78 },
  4: { x: 40, y: 48 },
  5: { x: 52, y: 78 },
  6: { x: 63, y: 46 },
  7: { x: 75, y: 78 },
  8: { x: 86, y: 46 },
  9: { x: 96, y: 18 },
};

function Landmark({ m, status, stars, isNextRecommended, showStartHere, onClick }) {
  const theme = THEMES[m.n] || THEMES[1];
  const p = PALETTE[theme.color] || PALETTE.yellow;
  const pos = POSITIONS[m.n] || { x: 50, y: 50 };
  const Art = LANDMARKS[theme.art] || LANDMARKS.cityHall;
  const { Icon } = theme;

  const isCompleted = status === "completed";
  const isUnlocked = status === "unlocked";
  const isPreview = status === "preview";
  const isLocked = status === "comingSoon";

  const fillColor = isLocked ? "#cbd5e1" : p.body;
  const accentColor = isLocked ? "#64748b" : p.stroke;

  const glowClass = isCompleted
    ? "drop-shadow-[0_0_14px_rgba(34,197,94,0.6)]"
    : isNextRecommended
    ? "drop-shadow-[0_0_18px_rgba(250,204,21,0.9)] animate-city-pulse"
    : isUnlocked
    ? "drop-shadow-[0_0_6px_rgba(250,204,21,0.35)]"
    : "";
  const dimClass = isLocked ? "opacity-55 grayscale" : isPreview ? "opacity-80" : "";

  return (
    <button
      onClick={() => !isLocked && onClick?.()}
      disabled={isLocked}
      className={`absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none transition-transform duration-200 ${
        isLocked ? "cursor-not-allowed" : "hover:-translate-y-[calc(50%+5px)] hover:scale-105"
      }`}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      aria-label={`Mission ${m.n}: ${m.t}, ${status}`}
      data-testid={`map-mission-${m.n}`}
    >
      {/* Landmark art */}
      <div className={`relative w-[108px] h-[96px] sm:w-[128px] sm:h-[114px] ${glowClass} ${dimClass}`}>
        <Art fill={fillColor} accent={accentColor} />

        {/* Number badge — integrated pennant */}
        <div className={`absolute -top-3 -left-3 w-10 h-10 rounded-full ${p.solid} ring-[3px] ring-white shadow-lg flex items-center justify-center font-black text-white text-base z-10`}>
          {m.n}
        </div>

        {/* Completed / Locked badge */}
        {isCompleted && (
          <div className="absolute -top-1 -right-1 bg-green-500 text-white w-7 h-7 rounded-full flex items-center justify-center shadow-lg z-10 ring-[2px] ring-white">
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
        {isLocked && (
          <div className="absolute -top-1 -right-1 bg-slate-400 text-white w-7 h-7 rounded-full flex items-center justify-center shadow-lg z-10 ring-[2px] ring-white">
            <Lock className="w-3.5 h-3.5" />
          </div>
        )}

        {/* Sparkles on completed */}
        {isCompleted && (
          <>
            <span className="absolute -top-3 left-6 text-yellow-400 text-sm animate-sparkle-1 select-none" aria-hidden="true">✦</span>
            <span className="absolute -top-1 right-8 text-yellow-300 text-xs animate-sparkle-2 select-none" aria-hidden="true">✦</span>
            <span className="absolute top-2 -right-3 text-yellow-500 text-sm animate-sparkle-3 select-none" aria-hidden="true">✦</span>
          </>
        )}

        {/* START HERE / NEXT STOP pennant */}
        {showStartHere && isNextRecommended && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-rose-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-lg whitespace-nowrap animate-bounce-gentle z-20">
            ⭐ Start Here!
          </div>
        )}
        {!showStartHere && isNextRecommended && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-lg whitespace-nowrap animate-bounce-gentle z-20">
            Next Stop →
          </div>
        )}
      </div>

      {/* Signpost label with wooden stake */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0.5 flex flex-col items-center pointer-events-none">
        <div className="w-0.5 h-2 bg-amber-800" aria-hidden="true" />
        <div
          className={`px-2.5 py-1 rounded-md shadow-md ring-1 whitespace-nowrap ${
            isCompleted
              ? "bg-green-100 ring-green-400 text-green-800"
              : isLocked
              ? "bg-slate-200 ring-slate-400 text-slate-600"
              : "bg-amber-50 ring-amber-400 text-amber-900"
          }`}
          style={{
            backgroundImage: isCompleted || isLocked ? undefined : "linear-gradient(to bottom, #fef3c7, #fde68a)",
          }}
        >
          <p className="text-[11px] sm:text-xs font-black flex items-center gap-1">
            <Icon className={`w-3 h-3 ${p.text}`} />
            {theme.label}
          </p>
        </div>
        {isCompleted && stars > 0 && (
          <div className="mt-1 flex gap-0.5 bg-white/95 rounded-full px-1.5 py-0.5 shadow ring-1 ring-yellow-200" aria-label={`${stars} out of 2 stars`}>
            {[1, 2].map((i) => (
              <Star key={i} className={`w-2.5 h-2.5 ${i <= stars ? "text-yellow-500 fill-yellow-500" : "text-gray-300 fill-gray-200"}`} />
            ))}
          </div>
        )}
      </div>

      {/* Hover tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-30" role="tooltip">
        <div className="bg-gray-900 text-white text-xs font-semibold rounded-xl px-3 py-2 shadow-xl max-w-[220px] whitespace-normal">
          <p className="font-bold text-yellow-300">Mission {m.n}</p>
          <p className="mt-0.5">{m.t}</p>
          <p className="mt-1 text-gray-300 text-[11px]">{m.d}</p>
          {isLocked ? (
            <p className="mt-1 text-slate-400 italic">Coming soon</p>
          ) : isPreview ? (
            <p className="mt-1 text-yellow-200">Tap to preview →</p>
          ) : isCompleted ? (
            <p className="mt-1 text-green-300">✓ Completed</p>
          ) : (
            <p className="mt-1 text-yellow-200">Tap to start →</p>
          )}
        </div>
      </div>
    </button>
  );
}

function JourneyRoad({ missions, completedSet }) {
  const ordered = [...missions].sort((a, b) => a.n - b.n);
  const pts = ordered.map((m) => POSITIONS[m.n]).filter(Boolean);
  if (pts.length < 2) return null;

  const buildPath = (points) =>
    points.reduce((acc, p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cx1 = prev.x + (p.x - prev.x) / 2;
      const cy1 = prev.y;
      const cx2 = prev.x + (p.x - prev.x) / 2;
      const cy2 = p.y;
      return `${acc} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${p.x} ${p.y}`;
    }, "");

  const baseD = buildPath(pts);

  let lastCompletedIdx = -1;
  for (let i = 0; i < ordered.length; i++) {
    if (completedSet.has(ordered[i].n)) lastCompletedIdx = i;
    else break;
  }
  const completedPts = ordered.slice(0, lastCompletedIdx + 1).map((m) => POSITIONS[m.n]);
  const completedD = completedPts.length >= 2 ? buildPath(completedPts) : "";

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="roadGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
        <filter id="roadShadow">
          <feDropShadow dx="0" dy="0.4" stdDeviation="0.5" floodOpacity="0.35" />
        </filter>
      </defs>
      {/* Sidewalk base (cream) */}
      <path d={baseD} fill="none" stroke="#fef3c7" strokeWidth="4.5" strokeLinecap="round" filter="url(#roadShadow)" />
      {/* Inner edge stripe */}
      <path d={baseD} fill="none" stroke="#fde68a" strokeWidth="3.2" strokeLinecap="round" />
      {/* Dashed gold centerline */}
      <path d={baseD} fill="none" stroke="#ca8a04" strokeWidth="0.55" strokeLinecap="round" strokeDasharray="0.8 1.8" />
      {/* Completed portion as solid gold */}
      {completedD && (
        <path d={completedD} fill="none" stroke="url(#roadGradient)" strokeWidth="3.2" strokeLinecap="round" />
      )}
      {/* Stepping-stone waypoint pips */}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="1.2" fill="#fffbeb" stroke="#ca8a04" strokeWidth="0.35" />
          <circle cx={p.x} cy={p.y} r="0.4" fill="#ca8a04" />
        </g>
      ))}
    </svg>
  );
}

function CityBackdrop() {
  return (
    <>
      {/* Sky gradient */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, #93c5fd 0%, #bfdbfe 25%, #e0f2fe 55%, #fef9c3 80%, #fde68a 100%)" }}
        aria-hidden="true"
      />

      {/* Distant mountains */}
      <svg className="absolute inset-x-0" style={{ top: "35%", height: "18%", width: "100%" }} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
        <polygon points="0,30 10,12 18,22 28,8 40,26 52,14 64,22 78,10 88,24 100,18 100,30" fill="#a5b4fc" opacity="0.55" />
        <polygon points="0,30 12,20 22,26 34,16 46,28 60,20 74,26 88,18 100,26 100,30" fill="#818cf8" opacity="0.45" />
      </svg>

      {/* Sun with rays */}
      <svg className="absolute" style={{ right: "4%", top: "3%", width: "12%", height: "20%" }} viewBox="0 0 100 100" aria-hidden="true">
        <g opacity="0.6">
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * Math.PI) / 6;
            const x1 = 50 + Math.cos(a) * 34;
            const y1 = 50 + Math.sin(a) * 34;
            const x2 = 50 + Math.cos(a) * 46;
            const y2 = 50 + Math.sin(a) * 46;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fde047" strokeWidth="3" strokeLinecap="round" />;
          })}
        </g>
        <circle cx="50" cy="50" r="28" fill="#fde047" />
        {/* Sunglasses sunny face */}
        <circle cx="42" cy="48" r="4" fill="#1f2937" />
        <circle cx="58" cy="48" r="4" fill="#1f2937" />
        <path d="M42 60 Q50 64 58 60" stroke="#f59e0b" strokeWidth="2" fill="none" strokeLinecap="round" />
      </svg>

      {/* Drifting clouds */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute animate-drift-slow"   style={{ top: "10%", left: "-12%" }}><Cloud size={110} /></div>
        <div className="absolute animate-drift-slower" style={{ top: "20%", left: "18%" }}><Cloud size={75} opacity={0.8} /></div>
        <div className="absolute animate-drift-slow"   style={{ top: "6%",  left: "48%" }}><Cloud size={130} /></div>
        <div className="absolute animate-drift-slower" style={{ top: "28%", left: "74%" }}><Cloud size={85} opacity={0.85} /></div>
      </div>

      {/* Rolling green hills (back → front) */}
      <svg className="absolute inset-x-0" style={{ bottom: "16%", height: "24%", width: "100%" }} viewBox="0 0 100 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 24 L0 14 Q15 4 30 12 Q50 20 70 8 Q85 0 100 10 L100 24 Z" fill="#bbf7d0" opacity="0.85" />
        <path d="M0 24 L0 18 Q20 10 40 16 Q60 22 80 14 Q92 10 100 16 L100 24 Z" fill="#86efac" />
      </svg>

      {/* Grass baseline with texture stripes */}
      <div className="absolute inset-x-0 bottom-0" style={{ height: "18%", background: "linear-gradient(to top, #22c55e 0%, #4ade80 70%, #86efac 100%)" }} aria-hidden="true" />
      <svg className="absolute inset-x-0 bottom-0" style={{ height: "6%", width: "100%" }} viewBox="0 0 100 6" preserveAspectRatio="none" aria-hidden="true">
        {Array.from({ length: 60 }).map((_, i) => {
          const x = i * 1.67 + Math.random() * 0.5;
          return <line key={i} x1={x} y1="6" x2={x} y2={4 + Math.random() * 1.5} stroke="#16a34a" strokeWidth="0.2" />;
        })}
      </svg>

      {/* Decorative trees sprinkled on both sides + between missions */}
      {[
        { x: 2,  scale: 0.9, kind: "pine"   },
        { x: 11, scale: 1.1, kind: "round"  },
        { x: 23, scale: 0.85, kind: "pine"  },
        { x: 34, scale: 1.15, kind: "round" },
        { x: 46, scale: 0.95, kind: "pine"  },
        { x: 57, scale: 1.0,  kind: "round" },
        { x: 69, scale: 1.1,  kind: "pine"  },
        { x: 80, scale: 0.9,  kind: "round" },
        { x: 92, scale: 1.05, kind: "pine"  },
      ].map((t, i) => (
        <div
          key={i}
          className="absolute pointer-events-none"
          style={{ left: `${t.x}%`, bottom: `${2 + (i % 3)}%`, transform: `translateX(-50%) scale(${t.scale})`, width: 36, height: 48 }}
          aria-hidden="true"
        >
          {t.kind === "pine" ? <PineTree /> : <RoundTree />}
        </div>
      ))}

      {/* Lamp posts along the journey */}
      {[
        { x: 11.5, y: 70 },
        { x: 34,   y: 68 },
        { x: 57.5, y: 68 },
        { x: 80,   y: 68 },
      ].map((l, i) => (
        <div key={i} className="absolute pointer-events-none" style={{ left: `${l.x}%`, top: `${l.y}%`, width: 18, height: 36 }} aria-hidden="true">
          <LampPost />
        </div>
      ))}

      {/* Tiny lemonade stand between M3 and M5 */}
      <div className="absolute pointer-events-none" style={{ left: "45%", top: "82%", width: 40, height: 42 }} aria-hidden="true">
        <LemonadeStand />
      </div>

      {/* Park bench between M1 and M2 */}
      <div className="absolute pointer-events-none" style={{ left: "12%", top: "86%", width: 32, height: 12 }} aria-hidden="true">
        <Bench />
      </div>

      {/* Balloons near Graduation Hall */}
      <div className="absolute pointer-events-none animate-float" style={{ left: "90%", top: "5%", width: 28, height: 40 }} aria-hidden="true">
        <Balloons />
      </div>

      {/* Banner stretched across top */}
      <div className="absolute left-1/2 -translate-x-1/2 top-2 px-5 py-1.5 bg-rose-500 text-white rounded-2xl shadow-lg font-black text-sm ring-2 ring-rose-300 pointer-events-none" style={{ letterSpacing: "0.04em" }}>
        🏙️ CEO CITY — JUNIOR CEO ADVENTURE
      </div>

      {/* Signpost "Next Mission →" between M2 and M3 */}
      <div className="absolute pointer-events-none" style={{ left: "24%", top: "70%" }} aria-hidden="true">
        <ArrowSignpost label="Next →" />
      </div>

      {/* Small park between M5 and M6 */}
      <div className="absolute pointer-events-none" style={{ left: "58%", top: "88%", width: 50, height: 8 }} aria-hidden="true">
        <div className="w-full h-full rounded-full bg-emerald-400/40" />
      </div>
    </>
  );
}

function Cloud({ size = 90, opacity = 0.95 }) {
  return (
    <svg width={size} height={size * 0.45} viewBox="0 0 100 45" style={{ opacity }} aria-hidden="true">
      <ellipse cx="25" cy="28" rx="20" ry="14" fill="#ffffff" />
      <ellipse cx="50" cy="22" rx="25" ry="18" fill="#ffffff" />
      <ellipse cx="75" cy="28" rx="22" ry="15" fill="#ffffff" />
      <ellipse cx="50" cy="34" rx="40" ry="9" fill="#ffffff" />
    </svg>
  );
}

function PineTree() {
  return (
    <svg viewBox="0 0 40 56" className="w-full h-full" aria-hidden="true">
      <rect x="17" y="40" width="6" height="14" fill="#78350f" rx="1" />
      <polygon points="20,2 6,22 14,22 4,38 34,38 24,22 32,22" fill="#16a34a" />
      <polygon points="20,5 9,22 13,22 7,34 31,34 25,22 29,22" fill="#22c55e" opacity="0.8" />
    </svg>
  );
}

function RoundTree() {
  return (
    <svg viewBox="0 0 40 50" className="w-full h-full" aria-hidden="true">
      <rect x="17" y="35" width="6" height="14" fill="#78350f" rx="1" />
      <circle cx="20" cy="22" r="15" fill="#22c55e" />
      <circle cx="13" cy="28" r="9"  fill="#4ade80" />
      <circle cx="27" cy="28" r="9"  fill="#4ade80" />
      <circle cx="20" cy="15" r="9"  fill="#16a34a" />
      <circle cx="16" cy="19" r="2"  fill="#ffffff" opacity="0.4" />
    </svg>
  );
}

function LampPost() {
  return (
    <svg viewBox="0 0 18 36" className="w-full h-full" aria-hidden="true">
      <rect x="8" y="6" width="2" height="28" fill="#44403c" />
      <rect x="6" y="34" width="6" height="2" fill="#44403c" />
      <path d="M9 6 L15 4" stroke="#44403c" strokeWidth="1.2" />
      <circle cx="15.5" cy="4" r="3" fill="#fde047" stroke="#44403c" strokeWidth="0.6" />
      <circle cx="15" cy="3.5" r="1" fill="#ffffff" opacity="0.7" />
    </svg>
  );
}

function LemonadeStand() {
  return (
    <svg viewBox="0 0 40 42" className="w-full h-full" aria-hidden="true">
      {/* Canopy stripes */}
      <path d="M2 18 L20 4 L38 18 Z" fill="#fde047" stroke="#ca8a04" strokeWidth="0.6" />
      <path d="M6 18 L20 8 L34 18" fill="none" stroke="#ef4444" strokeWidth="1.5" />
      {/* Counter */}
      <rect x="6" y="18" width="28" height="16" fill="#fef3c7" stroke="#ca8a04" strokeWidth="0.6" />
      <rect x="6" y="26" width="28" height="1.5" fill="#ca8a04" />
      {/* Sign */}
      <rect x="11" y="20" width="18" height="5" fill="#ffffff" stroke="#ca8a04" strokeWidth="0.4" rx="0.5" />
      <text x="20" y="24" textAnchor="middle" fontSize="3.5" fontWeight="900" fill="#ca8a04">LEMONADE</text>
      {/* Legs */}
      <rect x="6" y="34" width="2" height="7" fill="#78350f" />
      <rect x="32" y="34" width="2" height="7" fill="#78350f" />
      {/* Jug */}
      <ellipse cx="20" cy="32" rx="3" ry="2" fill="#fde047" stroke="#ca8a04" strokeWidth="0.4" />
    </svg>
  );
}

function Bench() {
  return (
    <svg viewBox="0 0 32 12" className="w-full h-full" aria-hidden="true">
      <rect x="0" y="2" width="32" height="3" fill="#78350f" rx="0.5" />
      <rect x="2" y="5" width="3" height="6" fill="#78350f" />
      <rect x="27" y="5" width="3" height="6" fill="#78350f" />
      <rect x="14" y="5" width="3" height="6" fill="#78350f" />
    </svg>
  );
}

function Balloons() {
  return (
    <svg viewBox="0 0 28 40" className="w-full h-full" aria-hidden="true">
      <ellipse cx="7" cy="10" rx="6" ry="7" fill="#ef4444" />
      <ellipse cx="20" cy="8" rx="6" ry="7" fill="#3b82f6" />
      <ellipse cx="14" cy="14" rx="6" ry="7" fill="#22c55e" />
      <path d="M7 17 Q8 24 10 30 L10 38" fill="none" stroke="#78350f" strokeWidth="0.4" />
      <path d="M20 15 Q19 22 16 30 L16 38" fill="none" stroke="#78350f" strokeWidth="0.4" />
      <path d="M14 21 Q14 28 13 30 L13 38" fill="none" stroke="#78350f" strokeWidth="0.4" />
    </svg>
  );
}

function ArrowSignpost({ label = "Next →" }) {
  return (
    <svg viewBox="0 0 42 40" className="w-[42px] h-[40px]" aria-hidden="true">
      <rect x="19" y="18" width="2" height="20" fill="#78350f" />
      <path d="M2 6 L34 6 L40 14 L34 22 L2 22 Z" fill="#fde047" stroke="#ca8a04" strokeWidth="0.8" />
      <text x="18" y="16" textAnchor="middle" fontSize="7" fontWeight="900" fill="#78350f">{label}</text>
    </svg>
  );
}

function LemonBotGuide({ completedCount, totalCount, nextLabel }) {
  const message =
    completedCount === 0
      ? "Hi! I'm LemonBot Jr. Tap a building to start your adventure!"
      : completedCount >= totalCount
      ? "You did it! Every stop in CEO City is done. Graduation time!"
      : `Great job! Your next stop is ${nextLabel || "coming up"}.`;

  return (
    <div className="absolute left-2 sm:left-4 bottom-3 flex items-end gap-2 z-20 pointer-events-none" aria-hidden="true">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-yellow-200 ring-2 ring-yellow-400 shadow-lg overflow-hidden shrink-0">
        <img src="/elementary/mascot/lemonbot-jr.png" alt="" className="w-full h-full object-contain p-0.5" loading="lazy" decoding="async" />
      </div>
      <div className="relative bg-white ring-2 ring-yellow-300 rounded-2xl px-3 py-2 shadow-lg max-w-[220px] sm:max-w-[280px]">
        <p className="text-xs sm:text-sm font-bold text-gray-900 leading-snug">{message}</p>
        <span className="absolute left-[-8px] bottom-4 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[10px] border-r-white" aria-hidden="true" />
        <span className="absolute left-[-11px] bottom-4 w-0 h-0 border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent border-r-[11px] border-r-yellow-300" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function CEOCityMap({
  missions = [],
  statusFor = () => "unlocked",
  stars = () => 0,
  onSelect,
  testId = "ceo-city-map",
}) {
  const [view, setView] = useState("map");

  const completedSet = useMemo(
    () => new Set(missions.filter((m) => statusFor(m) === "completed").map((m) => m.n)),
    [missions, statusFor]
  );

  const nextRecommendedMission = useMemo(() => {
    const ordered = [...missions].sort((a, b) => a.n - b.n);
    return ordered.find((m) => statusFor(m) === "unlocked") || null;
  }, [missions, statusFor]);

  const completedCount = completedSet.size;
  const showStartHere = completedCount === 0; // Only Mission 1 will bounce "Start Here!"

  if (view === "list") {
    return (
      <div data-testid={testId}>
        <Header view={view} setView={setView} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {missions.map((m) => {
            const status = statusFor(m);
            const theme = THEMES[m.n] || THEMES[1];
            const p = PALETTE[theme.color] || PALETTE.yellow;
            const { Icon } = theme;
            return (
              <button
                key={m.n}
                onClick={() => status !== "comingSoon" && onSelect?.(m)}
                disabled={status === "comingSoon"}
                className={`text-left rounded-3xl p-5 ring-2 transition-all flex items-start gap-4 ${
                  status === "completed"
                    ? "bg-green-50 ring-green-300"
                    : status === "unlocked"
                    ? "bg-white ring-yellow-400 hover:ring-yellow-500 hover:shadow-xl"
                    : status === "preview"
                    ? "bg-yellow-50/60 ring-yellow-200"
                    : "bg-slate-50 ring-slate-200 opacity-70"
                }`}
                data-testid={`map-list-${m.n}`}
              >
                <div className={`${p.solid} w-12 h-12 rounded-2xl flex items-center justify-center shrink-0`}>
                  <Icon className={`w-6 h-6 ${p.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Mission {m.n} · {theme.label}</p>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">{m.t}</h3>
                  <p className="text-sm text-gray-600 mt-1">{m.d}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <Header view={view} setView={setView} />

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-semibold">
        <LegendChip icon={<Play className="w-3 h-3" />} label="Unlocked" color="bg-yellow-100 text-yellow-800" />
        <LegendChip icon={<CheckCircle2 className="w-3 h-3" />} label="Completed" color="bg-green-100 text-green-800" />
        <LegendChip icon={<Eye className="w-3 h-3" />} label="Preview" color="bg-amber-100 text-amber-800" />
        <LegendChip icon={<Lock className="w-3 h-3" />} label="Coming soon" color="bg-slate-200 text-slate-700" />
      </div>

      {/* Horizontally-scrollable wrapper: on mobile, the city is wider than
          the viewport so landmarks stay tappable instead of squishing. */}
      <div className="w-full overflow-x-auto rounded-3xl ring-2 ring-yellow-300 shadow-2xl bg-sky-100">
        <div
          className="relative"
          style={{ minWidth: 1100, aspectRatio: "20 / 9", minHeight: 440 }}
          data-testid="ceo-city-map-canvas"
        >
          <CityBackdrop />
          <JourneyRoad missions={missions} completedSet={completedSet} />
          {missions.map((m) => (
            <Landmark
              key={m.n}
              m={m}
              status={statusFor(m)}
              stars={stars(m.n)}
              isNextRecommended={nextRecommendedMission?.n === m.n}
              showStartHere={showStartHere}
              onClick={() => onSelect?.(m)}
            />
          ))}
          <LemonBotGuide
            completedCount={completedCount}
            totalCount={missions.length}
            nextLabel={
              nextRecommendedMission
                ? THEMES[nextRecommendedMission.n]?.label || `Mission ${nextRecommendedMission.n}`
                : ""
            }
          />

          {/* Mobile scroll hint (only shows if parent is scrollable) */}
          <div className="sm:hidden absolute right-3 top-3 bg-white/90 rounded-full px-2.5 py-1 text-[10px] font-bold text-gray-700 shadow animate-pulse">
            ← scroll →
          </div>
        </div>
      </div>

      <style>{`
        @keyframes city-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 18px rgba(250,204,21,0.9)); }
          50%      { transform: scale(1.06); filter: drop-shadow(0 0 26px rgba(250,204,21,1)); }
        }
        .animate-city-pulse { animation: city-pulse 2s ease-in-out infinite; }

        @keyframes bounce-gentle {
          0%, 100% { transform: translate(-50%, 0); }
          50%      { transform: translate(-50%, -4px); }
        }
        .animate-bounce-gentle { animation: bounce-gentle 1.3s ease-in-out infinite; }

        @keyframes drift-slow {
          from { transform: translateX(0); }
          to   { transform: translateX(140vw); }
        }
        @keyframes drift-slower {
          from { transform: translateX(0); }
          to   { transform: translateX(140vw); }
        }
        .animate-drift-slow   { animation: drift-slow   90s linear infinite; }
        .animate-drift-slower { animation: drift-slower 140s linear infinite; }

        @keyframes sparkle-1 {
          0%, 100% { opacity: 0; transform: translateY(0) scale(0.6); }
          50%      { opacity: 1; transform: translateY(-6px) scale(1.1); }
        }
        @keyframes sparkle-2 {
          0%, 100% { opacity: 0; transform: translateY(0) scale(0.6); }
          50%      { opacity: 1; transform: translateY(-4px) scale(1); }
        }
        @keyframes sparkle-3 {
          0%, 100% { opacity: 0; transform: translateY(0) scale(0.6); }
          50%      { opacity: 1; transform: translateY(-5px) scale(1.2); }
        }
        .animate-sparkle-1 { animation: sparkle-1 2.2s ease-in-out infinite; }
        .animate-sparkle-2 { animation: sparkle-2 2.2s ease-in-out 0.5s infinite; }
        .animate-sparkle-3 { animation: sparkle-3 2.2s ease-in-out 1s infinite; }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function Header({ view, setView }) {
  return (
    <div className="flex items-start sm:items-center justify-between gap-3 mb-4 flex-col sm:flex-row">
      <div>
        <h2 className="text-2xl sm:text-3xl font-black text-gray-900">Welcome to CEO City</h2>
        <p className="text-sm text-gray-600 mt-1">
          Tap a place to continue your Junior CEO adventure.
        </p>
      </div>
      <ViewToggle view={view} setView={setView} />
    </div>
  );
}

function LegendChip({ icon, label, color }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${color}`}>
      {icon}
      {label}
    </span>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div className="inline-flex items-center bg-white ring-1 ring-yellow-200 rounded-full p-1 shadow-sm">
      <button
        type="button"
        onClick={() => setView("map")}
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${
          view === "map" ? "bg-yellow-500 text-white shadow" : "text-gray-700 hover:text-gray-900"
        }`}
        data-testid="ceo-city-map-toggle-map"
        aria-pressed={view === "map"}
      >
        <MapIcon className="w-3.5 h-3.5" />
        Map
      </button>
      <button
        type="button"
        onClick={() => setView("list")}
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${
          view === "list" ? "bg-yellow-500 text-white shadow" : "text-gray-700 hover:text-gray-900"
        }`}
        data-testid="ceo-city-map-toggle-list"
        aria-pressed={view === "list"}
      >
        <List className="w-3.5 h-3.5" />
        List
      </button>
    </div>
  );
}
